/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Component, ElementRef, Input, Output, EventEmitter, HostBinding, HostListener, OnInit } from '@angular/core';
import { PickerOptions } from './util/date-range-picker-options';

// set up moment and moment-range
import * as Moment from 'moment';
//import { extendMoment } from 'moment-range';
var em = require('moment-range');
var moment = em.extendMoment(Moment);

@Component({
    selector: 'date-range-picker',
    templateUrl: './date-range-picker.component.html'
})
export class DateRangePickerComponent implements OnInit {
  //inputs
  @Input() options: PickerOptions;
  @Input() flipRight: boolean = false;

  //outputs
  @Output() rangeSelected = new EventEmitter();

  // view variables
  showControl: boolean;
  showCalendars: boolean = true;
  range: string = '';

  calendarFrom: any = {};
  calendarTo: any = {};

  startDateFormatted: any;
  endDateFormatted: any;
  fromMonth: number;
  toMonth: number;
  fromYear: number;
  toYear: number;
  format: string;

  customRangeState: any = {
    currentStart: null,
    currentEnd: null,
    currentCached: false,
    startSelected: null,
    endSelected: null
  }

  defaultRanges: any[] =
  [
    {
      name: 'Today',
      numDays: 1,
      start: moment.utc(),
      end: moment.utc(),
      active: false
    },
    // {
    //   name: 'Yesterday',
    //   numDays: -1,
    //   start: moment.utc().subtract(1, 'days'),
    //   end: moment.utc().subtract(1, 'days'),
    //   active: false
    // },
    {
      name: 'Last 7 Days',
      numDays: 7,
      start: moment.utc().subtract(6, 'days'),
      end: moment.utc(),
      active: false
    },
    {
      name: 'Last 14 Days',
      numDays: 14,
      start: moment.utc().subtract(13, 'days'),
      end: moment.utc(),
      active: false
    },
    {
      name: 'Last 30 Days',
      numDays: 30,
      start: moment.utc().subtract(29, 'days'),
      end: moment.utc(),
      active: false
    },
    {
      name: 'Last 90 Days',
      numDays: 90,
      start: moment.utc().subtract(89, 'days'),
      end: moment.utc(),
      active: false
    }
    // {
    //   name: 'This month',
    //   value: {
    //     start: moment.utc().startOf('month'),
    //     end: moment.utc().endOf('month')
    //   }
    // },
    // {
    //   name: 'Last Month',
    //   value: {
    //     start: moment.utc().subtract(1, 'month').startOf('month'),
    //     end: moment.utc().subtract(1, 'month').endOf('month')
    //   }
    // }
  ];



  constructor(private elem: ElementRef) {
  }

  /**
   * Detect clicking on the control or outside the control to activate / deactivate
   */
  //@HostListener('document:mousedown', ['$event'])
  @HostListener('document:mouseup', ['$event'])
  handleOutsideClick(event) {
    let current: any = event.target;
    let host: any = this.elem.nativeElement;
    if (host.compareDocumentPosition) {
      if (host.compareDocumentPosition(current) & Node.DOCUMENT_POSITION_CONTAINED_BY) {
        return this.showControl = true;
      }
    }

    if (this.showControl) {
      if (this.customRangeState.startSelected) {
        this.restoreOldDates();
      }
      else {
        this.detectDefaultRange();
      }
      this.hideControl();
    }
  }

  ngOnInit() {
    if (!this.options.format) {
      this.format = 'MM/DD/YYYY';
    }
    else {
      this.format = this.options.format;
    }

    if (!this.options.startDate || !this.options.endDate) {
      this.startDateFormatted = moment.utc().format(this.format);
      this.endDateFormatted = moment.utc().format(this.format);
    }
    else {
      this.startDateFormatted = moment.utc(this.options.startDate).format(this.format);
      this.endDateFormatted = moment.utc(this.options.endDate).format(this.format);
    }

    this.initRange();
    this.detectDefaultRange();

    // only generate calendar data if needed
    if (this.showCalendars) {
      this.generateCalendars();
      this.setDayStatus();
    }
  }

  /**
   * Sets range for rendering two calendars when initialized
   */
  private initRange() {
    this.setRange(moment.utc(this.startDateFormatted, this.format), moment.utc(this.endDateFormatted, this.format));
  }

  /**
   * A default range is selected
   */
  private defaultRange(range: any) {
    this.showCalendars = false;
    this.setRange(range.start, range.end);
    this.range = range.name;
    this.detectDefaultRange(); // disables old range
    this.emitRangeSelected();
  }

  /**
   * Custom range was selected, show calendars and deactivate the defaults
   */
  public customRange() {
    this.showCalendars = true;
    this.generateCalendars();
    this.setDayStatus();
    this.defaultRanges.forEach((r) => {
      r.active = false;
    });
  }

  /**
   * Given a start and end (both moment objects), set up range variables for use
   */
  private setRange(start, end) {
    // set up all date variables
    this.fromMonth = start.month();
    this.fromYear = start.year();
    this.toMonth = end.month();
    this.toYear = end.year();
    this.startDateFormatted = moment.utc(start).format(this.format);
    this.endDateFormatted = moment.utc(end).format(this.format);

    if (this.fromMonth == this.toMonth && this.fromYear == this.toYear) {
      // if they are the same, we want to show current and previous month
      this.fromMonth -= 1;
    }
    else {
      this.fromMonth = this.toMonth - 1;
    }

    if (this.fromMonth < 0) {
      // was january and need to turn it into december of the previous year
      this.fromMonth = 11;
      // this condition is only true if the current day in january is > the day range previously select
      // so if the date is 1/8 and the user had l30 selected this would be true
      // but have l7 selected wasn't making the bug happen.
      if (this.fromYear == this.toYear) {
        this.fromYear -= 1;
      }
    }
  }

  /**
   * public API to refresh the state of the control.
   * Currently used when a the date is manually reset and this has to be re-drawn
   */
  public refresh(){
    this.startDateFormatted = moment.utc(this.options.startDate).format(this.format);
    this.endDateFormatted = moment.utc(this.options.endDate).format(this.format);
    this.detectDefaultRange();
  }

  /**
   * With any change to the selected range (whether default or custom), check to see if the
   * selected range is one of the defaults and update the control accordingly
   */
  private detectDefaultRange() {
    this.showCalendars = true;
    this.range = `${this.startDateFormatted} - ${this.endDateFormatted}`;
    this.defaultRanges.forEach((range) => {
      if (moment.utc(range.start).format(this.format) == this.startDateFormatted && moment.utc(range.end).format(this.format) == this.endDateFormatted) {
        range.active = true;
        this.range = range.name;
        this.showCalendars = false;
      }
      else {
        range.active = false;
      }
    });
  }

  private hideControl(){
    this.customRangeState.startSelected=null;
    this.customRangeState.endSelected = null;
    this.showControl = false;
  }

  /**
   * Emits change message to notify parent component
   */
  emitRangeSelected() {
    let message: any = {};
    message.start = moment.utc(this.startDateFormatted, this.format).toDate();
    message.end = moment.utc(this.endDateFormatted, this.format).toDate();

    if (!this.showCalendars) {
      // add num days so future refreshes aren't tied to a specific date range
      this.defaultRanges.forEach((range) => {
        if (moment.utc(range.start).format(this.format) == this.startDateFormatted && moment.utc(range.end).format(this.format) == this.endDateFormatted) {
          message.numDays = range.numDays;
        }
      });
    }
    this.rangeSelected.emit(message);
    this.hideControl();
  }

  // --------------------------------------------------------------------------------------------------------------------
  // CALENDAR specific interaction

  private monthChange(direction: number) {
    this.fromMonth += direction;
    this.toMonth += direction;

    if (this.fromMonth < 0) {
      this.fromMonth = 11;
      this.fromYear -= 1;
    }
    else if (this.fromMonth > 11) {
      this.fromMonth = 0;
      this.fromYear += 1;
    }

    if (this.toMonth < 0) {
      this.toMonth = 11;
      this.toYear -= 1;
    }
    else if (this.toMonth > 11) {
      this.toMonth = 0;
      this.toYear += 1;
    }

    this.generateCalendars();
    this.setDayStatus();
  }

  private selectDay(day) {
    if (!this.customRangeState.startSelected) {
      // clicking first date
      this.cacheOldDates();
      this.customRangeState.startSelected = day;
      this.startDateFormatted = moment(day).format(this.format);
      this.endDateFormatted = '';
      this.setDayStatus()
    }
    else {
      // clicking second date, make sure it's more recent than the start date
      if (day >= this.customRangeState.startSelected) {
        this.customRangeState.endSelected = day;
        this.endDateFormatted = moment(day).format(this.format);
        this.range = `${this.startDateFormatted} - ${this.endDateFormatted}`;
        this.setDayStatus();
        this.emitRangeSelected();
      }
    }
  }

  private cacheOldDates() {
    if (!this.customRangeState.currentCached) {
      this.customRangeState.currentStart = this.startDateFormatted;
      this.customRangeState.currentEnd = this.endDateFormatted;
      this.customRangeState.currentCached = true;
    }
  }

  private restoreOldDates() {
    this.startDateFormatted = this.customRangeState.currentStart;
    this.endDateFormatted = this.customRangeState.currentEnd;
    this.customRangeState.currentCached = false;
    this.customRangeState.currentStart = null;
    this.customRangeState.currentEnd = null;
    this.customRangeState.startSelected = null;
    this.detectDefaultRange();
    if (this.showCalendars) {
      this.generateCalendars();
      this.setDayStatus();
    }
  }

  /**
   * Generates calendars based on current selected date range
   */
  private generateCalendars() {
    // from calendar
    let startDate = moment([this.fromYear, this.fromMonth]);
    //let startDate = moment(this.startDateFormatted, this.format);
    let monthRange = moment.range(moment(startDate).startOf('month'), moment(startDate).add(60, 'd'));
    let weeksRange = this.getWeeksRange(this.getWeekNumbers(monthRange), this.fromYear, this.fromMonth);

    // create array of weeks (which are arrays of days) for the from calendar
    this.calendarFrom.weeks = [];
    weeksRange.map((week: any) => {
      this.calendarFrom.weeks.push(Array.from(week.by('days')));
    });

    this.calendarFrom.header = `${moment.monthsShort()[this.fromMonth]} ${this.fromYear}`;

    // to calendar
    let endDate = moment([this.toYear, this.toMonth]);
    //let endDate = moment(this.endDateFormatted, this.format);
    monthRange = moment.range(moment(endDate).startOf('month'), moment(endDate).add(60, 'd'));
    weeksRange = this.getWeeksRange(this.getWeekNumbers(monthRange), this.toYear, this.toMonth);

    // create array of weeks (which are arrays of days) for the to calendar
    this.calendarTo.weeks = [];
    weeksRange.map((week: any) => {
      this.calendarTo.weeks.push(Array.from(week.by('days')));
    });

    this.calendarTo.header = `${moment.monthsShort()[this.toMonth]} ${this.toYear}`;

    this.range = `${this.startDateFormatted} - ${this.endDateFormatted}`;
  }

  /**
   * Set status on days for css formatting
   */
  setDayStatus() {
    let currentRange = moment.range(moment.utc(this.startDateFormatted, this.format), moment.utc(this.endDateFormatted, this.format));
    this.calendarFrom.weeks.forEach((week: any) => {
      week.forEach((day: any) => {
        // active days are available for clicking and valid in selected ranges
        if (moment(day).month() == this.fromMonth) {
          day.active = true;
        }
        else {
          day.active = false;
        }

        // selected date is the date currently marking the beginning or end of the range
        if ((moment(day).format(this.format) == this.startDateFormatted || moment(day).format(this.format) == this.endDateFormatted) && day.active) {
          day.selected = true;
        }
        else {
          day.selected = false;
        }

        // in range is a date between the two selected dates
        if (currentRange.contains(day) && day.active && !day.selected) {
          day.inRange = true;
        }
        else {
          day.inRange = false;
        }
      });
    });

    this.calendarTo.weeks.forEach((week: any) => {
      week.forEach((day: any) => {
        // active days are available for clicking and valid in selected ranges
        if (moment(day).month() == this.toMonth) {
          day.active = true;
        }
        else {
          day.active = false;
        }

        // selected date is the date currently marking the beginning or end of the range
        if ((moment(day).format(this.format) == this.startDateFormatted || moment(day).format(this.format) == this.endDateFormatted) && day.active) {
          day.selected = true;
        }
        else {
          day.selected = false;
        }

        // in range is a date between the two selected dates
        if (currentRange.contains(day) && day.active && !day.selected) {
          day.inRange = true;
        }
        else {
          day.inRange = false;
        }
      });
    });
  }

  /**
   * Gets the range of days for each week in a given year + month
   */
  getWeeksRange(weeks: any, year: any, month: any) {
    let weeksRange = [];

    for (let i = 0, len = weeks.length; i < len; i++) {
      let week = weeks[i];
      let firstWeekDay, lastWeekDay;
      if (i > 0 && week < weeks[i - 1]) {
        firstWeekDay = moment([year, month]).add(1, "year").week(week).day(0);
        lastWeekDay = moment([year, month]).add(1, "year").week(week).day(6);
      }
      else {
        firstWeekDay = moment([year, month]).week(week).day(0);
        lastWeekDay = moment([year, month]).week(week).day(6);
      }
      let weekRange = moment.range(firstWeekDay, lastWeekDay);
      weeksRange.push(weekRange);
    }
    return weeksRange;
  }

  /**
   * Determines the week numbers needed for rendering a specific year/month calendar
   */
  private getWeekNumbers(monthRange: any) {
    let weekNumbers = [];
    let indexOf = [].indexOf || function(item) {
      for (var i = 0, l = this.length; i < l; i++) {
        if (i in this && this[i] === item) {
          return i;
        }
      }
      return -1;
    };
    let temp = Array.from(monthRange.by('days'));
    temp.forEach((d: any) => {
      let ref;
      if (weekNumbers.length < 6 && (ref = d.week(), indexOf.call(weekNumbers, ref)) < 0) {
        weekNumbers.push(d.week());
      }
    });
    return weekNumbers;
  }
}



// WEBPACK FOOTER //
// ./client/app/global/date-range/date-range-picker.component.ts